import { sortByOrder } from '@strength/utils';
/**
 * Resolve a pick from the exercise dropdown (a block layer `workoutExerciseId`)
 * to its layer-stable identity. Returns null when the layer no longer holds it.
 */
export function resolvePrintedExerciseTarget(layer, workoutExerciseId) {
    const picked = layer.exerciseAdds.find((exercise) => exercise.workoutExerciseId === workoutExerciseId);
    if (!picked)
        return null;
    const sectionName = layer.sectionAdds.find((section) => section.workoutSectionId === picked.workoutSectionId)?.name ?? '';
    // Position among the section's exercises sharing this library id, ordered the
    // same way the dropdown lists them. Serializing a workout writes one
    // exerciseAdds row per entry copy, so the same workoutExerciseId can appear
    // more than once; count each exercise once, matching how the merged workout
    // deduplicates those copies.
    const seenIds = new Set();
    const repeats = sortByOrder(layer.exerciseAdds.filter((exercise) => {
        const isRepeat = exercise.workoutSectionId === picked.workoutSectionId &&
            exercise.libraryExerciseId === picked.libraryExerciseId &&
            !seenIds.has(exercise.workoutExerciseId);
        if (isRepeat)
            seenIds.add(exercise.workoutExerciseId);
        return isRepeat;
    }));
    return {
        libraryExerciseId: picked.libraryExerciseId,
        exerciseName: picked.exerciseName,
        sectionName,
        occurrenceIndex: repeats.indexOf(picked),
    };
}
/**
 * Exercises a section entry contributes to the printed card.
 *
 * An exercise that is both a superset member and an alternate group member in
 * one section is listed in both entries; the superset entry is the one that
 * renders elsewhere in the app, so only that copy is kept and the alternate
 * group still contributes its other members.
 */
function getEntryExercises(entry, sectionEntries) {
    if (entry.__type === 'WorkoutExercise')
        return [entry];
    if (entry.__type === 'WorkoutSuperset') {
        return entry.exercises.map((grouped) => grouped.exercise);
    }
    const supersetExerciseIds = new Set(sectionEntries
        .filter((other) => other.__type === 'WorkoutSuperset')
        .flatMap((superset) => superset.exercises.map((grouped) => grouped.workoutExerciseId)));
    return entry.exercises
        .map((grouped) => grouped.exercise)
        .filter((exercise) => !supersetExerciseIds.has(exercise.workoutExerciseId));
}
/**
 * Find the exercise to print in one athlete's resolved workout, or null when
 * that workout does not contain it. Returns at most one exercise, so an athlete
 * never gets two rows in the table.
 *
 * Pass a `target` to match across layers; pass `workoutExerciseId` alone (target
 * null) to match the id directly, which only works for workouts built from the
 * layer the pick came from.
 */
export function findPrintedExercise(workout, target, workoutExerciseId = null) {
    for (const section of workout.sections) {
        if (target?.sectionName && section.name !== target.sectionName)
            continue;
        const matches = sortByOrder(section.entries
            .flatMap((entry) => getEntryExercises(entry, section.entries))
            .filter((exercise) => target
            ? exercise.libraryExerciseId === target.libraryExerciseId
            : exercise.workoutExerciseId === workoutExerciseId));
        const match = matches[target?.occurrenceIndex ?? 0];
        if (match)
            return match;
    }
    return null;
}
//# sourceMappingURL=find-printed-exercise.js.map